/**
 * Party follower: replays the leader's path history at `distance * slot` px behind (slot 1 = first
 * follower, slot 2 = second). Position is driven directly (no collision re-check: the leader already
 * walked there). Faces its movement direction and stops cleanly when the leader stops.
 */
import type Phaser from 'phaser';
import { ActorSystem, StepOrder, type Steppable } from './ActorSystem';
import { SceneEvents } from './sceneEvents';
import type { Actor } from './Actor';
import { TrailFollowLogic } from './trail';

export interface FollowerOptions {
  /** 1 = directly behind the leader, 2 = behind the first follower. Default 1. */
  slot?: number;
  /** Trail distance per slot in px (default 28). */
  distance?: number;
  /** Top speed px/s (default 1.3 × max(leader, self) so it can catch up without overshooting). */
  speed?: number;
}

export class Follower implements Steppable {
  readonly logic: TrailFollowLogic;
  readonly slot: number;
  private destroyed = false;
  private readonly system: ActorSystem;
  private readonly onShutdown = () => this.destroy();

  constructor(readonly scene: Phaser.Scene, readonly actor: Actor, readonly leader: Actor, opts: FollowerOptions = {}) {
    this.slot = opts.slot ?? 1;
    const distance = (opts.distance ?? 28) * this.slot;
    const speed = opts.speed ?? Math.max(leader.speed, actor.speed) * 1.3;
    this.logic = new TrailFollowLogic({ distance, speed });
    const start = this.logic.seed(leader.fx, leader.fy, leader.facing);
    const place = actor.isBlockedAt(start.x, start.y) ? { x: leader.fx, y: leader.fy } : start;
    actor.setPositionDriven(place.x, place.y, false, leader.facing);
    this.system = ActorSystem.get(scene);
    this.system.add(this, StepOrder.FOLLOWER + this.slot);
    scene.events.once(SceneEvents.SHUTDOWN, this.onShutdown);
  }

  /** Snap behind the leader (after a teleport / map entry). */
  reset(): void {
    const start = this.logic.seed(this.leader.fx, this.leader.fy, this.leader.facing);
    const place = this.actor.isBlockedAt(start.x, start.y) ? { x: this.leader.fx, y: this.leader.fy } : start;
    this.actor.setPositionDriven(place.x, place.y, false, this.leader.facing);
  }

  step(dtMs: number): void {
    if (this.destroyed) return;
    const out = this.logic.update(this.leader.fx, this.leader.fy, this.actor.fx, this.actor.fy, dtMs);
    this.actor.setPositionDriven(out.x, out.y, out.moving, out.facing ?? undefined);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.system.remove(this);
    this.actor.releaseDriven();
    this.scene.events.off(SceneEvents.SHUTDOWN, this.onShutdown);
  }
}
